import React, {useState} from "react";
import s from './TextBox.module.scss';
import {FontIcon, Icon} from "../FontIcon";
import {generateUUID} from "../../../services/misc";

export const autoCompleteValues = [
  "on",
  "off",
  "address-line1",
  "address-line2",
  "address-line3",
  "address-level1",
  "address-level2",
  "address-level3",
  "address-level4",
  "street-address",
  "country",
  "country-name",
  "postal-code",
  "name",
  "additional-name",
  "family-name",
  "given-name",
  "honorific-prefix",
  "honorific-suffix",
  "nickname",
  "organization-title",
  "username",
  "new-password",
  "current-password",
  "bday",
  "bday-day",
  "bday-month",
  "bday-year",
  "sex",
  "one-time-code",
  "organization",
  "cc-name",
  "cc-given-name",
  "cc-additional-name",
  "cc-family-name",
  "cc-number",
  "cc-exp",
  "cc-exp-month",
  "cc-exp-year",
  "cc-csc",
  "cc-type",
  "transaction-currency",
  "transaction-amount",
  "language",
  "url",
  "email",
  "photo",
  "tel",
  "tel-country-code",
  "tel-national",
  "tel-area-code",
  "tel-local",
  "tel-local-prefix",
  "tel-local-suffix",
  "tel-extension",
  "impp"
] as const;

export type TextBoxAutoCompleteValue = typeof autoCompleteValues[number];

export const inputModeValues = [
  'none',
  'text',
  'tel',
  'url',
  'email',
  'numeric',
  'decimal',
  'search',
] as const;

export type TextBoxInputModeValue = typeof inputModeValues[number];

interface ITextBoxProps{
  autoCapitalize?: boolean;
  autoComplete?: TextBoxAutoCompleteValue;
  disabled?: boolean;
  errorMessages?: string[];
  inputMode?: TextBoxInputModeValue;
  isEmail?: boolean;
  isErrorState?: boolean;
  isPassword?: boolean;
  isRequired?: boolean;
  label?: string;
  note?: string,
  onBlur?: () => void;
  onChange?: (newValue: string) => void;
  placeHolder?: string;
  value?: string;
}

export default function TextBox({
  autoCapitalize = false,
  autoComplete,
  disabled,
  errorMessages,
  inputMode,
  isEmail,
  isErrorState = false,
  isPassword = false,
  isRequired = false,
  label,
  note,
  onBlur,
  onChange,
  placeHolder,
  value,
}: ITextBoxProps) {
  const id = generateUUID();
  const [shouldShowPassword, setShouldShowPassword] = useState(false);
  const labelElement = createLabelElement(isErrorState, label, isRequired, id);
  const noteElement = createNoteElement(isErrorState, note);
  const errorMessageElement = createErrorMessageElement(!!isErrorState, errorMessages);
  const eyeballIcon: Icon = shouldShowPassword ? `icon-eye-slash` : `icon-eye`;

  const passwordEyeballElement = isPassword
    ?
    <span
      onClick={()=> setShouldShowPassword(!shouldShowPassword) }
      className={s.passwordEyeball}
      tabIndex={0}>
      <FontIcon icon={eyeballIcon} additionalClassName={s.textBoxEyeball}/>
    </span>
    : null;

  let inputType = "text";

  if (isPassword && !shouldShowPassword) {
    inputType = "password";
  } else if (isEmail) {
    inputType = "email";
  }

  return (
    <div className={s.textBox}>
      {labelElement}
      {noteElement}
      {errorMessageElement}
      <input
        autoCapitalize={autoCapitalize ? "on" : "off"}
        autoComplete={autoComplete}
        disabled={disabled}
        id={id}
        inputMode={inputMode}
        onBlur={onBlur}
        onChange={ onChange ? (e) => onChange(e.target.value): undefined }
        placeholder={placeHolder}
        spellCheck={false}
        type={inputType}
        value={value}
      />
      {passwordEyeballElement}
    </div>
  );
}

function createLabelElement(isErrorState?: boolean, label?: string, isRequired?: boolean, inputId?: string){
  if(!label){ return; }
  const errorClassName = isErrorState ? s.inputError : ``;
  const requiredEl = isRequired ? <span>(Required)</span> : null;
  return <label htmlFor={inputId} className={errorClassName}>{label} {requiredEl}</label>;
}

function createNoteElement(isErrorState?: boolean, note?: string) {
  if (!isErrorState) {
    return <p className={s.inputNote}>{note}</p>
  }
}

function createErrorMessageElement( isErrorState: boolean, errorMessages?: string[],){
  if(!isErrorState || !errorMessages || errorMessages.length <= 0) { return; }
  const errorMessageEls = errorMessages.map((e, index) => <><span key={index}>{e}</span>&nbsp;</>);
  return <div className={s.inputErrorMessage} role={"alert"}>
    <FontIcon icon={"icon-exclamation-triangle"} additionalClassName={s.textBoxErrorIcon}/>
    <div className={s.messages}>
      {errorMessageEls}
    </div>
  </div>;
}
